import React from 'react';
import { Alert, Box, Chip, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ScanCameraMeasurementBasis } from '../cameraMeasurement/ScanCameraMeasurementBasis';
import { ScanControlRelationDisclosure } from '../safetyCases/ScanControlRelationDisclosure';
import type { ScanMeasurementSource } from '../../domain/cameraMeasurement/scanMeasurementIdentity';
import type { CameraActivityMeasurementResponse } from '../../services/cameraActivityMeasurementApi';
import {
  CANONICAL_REPORT_TITLE,
  type CanonicalSafetyScanReportView,
} from '../../domain/safetyScanReport/canonicalSafetyScanReportPresenter';
import {
  CAMERA_MEASUREMENT_PRIVACY,
  SCAN_MEASUREMENT_CLAIM_BOUNDARY,
} from '../../domain/cameraMeasurement/cameraMeasurementPresentation';

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker?: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="section"
      data-testid={`canonical-scan-report-${id}`}
      sx={{
        bgcolor: '#fff',
        border: '1px solid #e3e8ef',
        borderRadius: 2,
        p: { xs: 1.5, md: 2 },
      }}
    >
      {kicker ? (
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
          {kicker}
        </Typography>
      ) : null}
      <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.12, display: 'block' }}>
        {title}
      </Typography>
      <Box sx={{ mt: 1 }}>{children}</Box>
    </Box>
  );
}

function LifecycleStrip({ view }: { view: CanonicalSafetyScanReportView }) {
  return (
    <Box
      data-testid="canonical-scan-report-lifecycle-strip"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, minmax(0, 1fr))' },
        gap: 0.75,
      }}
    >
      {view.lifecycleStrip.map((item) => (
        <Box
          key={item.id}
          data-testid={`canonical-scan-report-strip-${item.id}`}
          sx={{
            bgcolor: '#f4f7fb',
            borderTop: '3px solid #2467db',
            borderRadius: 1,
            px: 1,
            py: 0.75,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.08, color: '#52677f' }}>
            {item.label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 750 }}>{item.state}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">{item.detail}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function Fact({
  label,
  value,
  testId,
  muted,
}: {
  label: string;
  value: string;
  testId: string;
  muted?: boolean;
}) {
  return (
    <Box data-testid={testId}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: muted ? 500 : 700 }} color={muted ? 'text.secondary' : 'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
}

export function CanonicalSafetyScanReport({
  view,
  measurementSource,
  measurementOverride = null,
  measurementDefaultExpanded,
}: {
  view: CanonicalSafetyScanReportView;
  measurementSource: ScanMeasurementSource;
  measurementOverride?: CameraActivityMeasurementResponse | null;
  measurementDefaultExpanded?: boolean;
}) {
  return (
    <Stack spacing={1.5} data-testid="canonical-safety-scan-report" data-report-id={view.displayedReportId}>
      <Box>
        <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.14 }}>
          {CANONICAL_REPORT_TITLE}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }} data-testid="canonical-scan-report-headline">
          {view.conditionLabel}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
          <Chip size="small" label={view.areaLabel} data-testid="canonical-scan-report-area" />
          <Chip size="small" label={view.windowLabel || 'Measurement period not recorded'} data-testid="canonical-scan-report-period" />
          <Chip
            size="small"
            color={view.lifecycle === 'Signed' ? 'success' : view.lifecycle === 'Verified' ? 'primary' : 'default'}
            label={view.lifecycle}
            data-testid="canonical-scan-report-lifecycle"
          />
          {view.isCurrentScan ? (
            <Chip size="small" color="primary" label="Current Scan" data-testid="canonical-scan-report-current-badge" />
          ) : (
            <Chip size="small" variant="outlined" label="Not Current Scan" data-testid="canonical-scan-report-not-current-badge" />
          )}
          {view.isLatestSigned ? (
            <Chip size="small" color="success" label="Latest Signed" data-testid="canonical-scan-report-latest-signed-badge" />
          ) : null}
        </Stack>
      </Box>

      <LifecycleStrip view={view} />

      <Section id="result" kicker="A" title="What was observed">
        <Fact label="Operating finding" value={view.findingLabel} testId="canonical-scan-report-finding" />
        <Fact label="Confirmed reviewed occurrences" value={view.confirmedLabel} testId="canonical-scan-report-confirmed" />
        <Fact label="Usable observation hours" value={view.usableHoursLabel} testId="canonical-scan-report-hours" />
        <Fact
          label="Rate"
          value={view.rateLabel}
          testId="canonical-scan-report-rate"
          muted={!view.rateIssuable}
        />
        <Fact label="Recurrence" value={view.recurrenceLabel} testId="canonical-scan-report-recurrence" />
      </Section>

      <Section id="verification" title="Verification">
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" data-testid="canonical-scan-report-verification-counts">
          <Chip size="small" label={`Reviewed ${view.verification.reviewed}`} data-testid="canonical-scan-report-reviewed" />
          <Chip size="small" label={`To confirm ${view.verification.toConfirm}`} data-testid="canonical-scan-report-to-confirm" />
          <Chip size="small" label={`Confirmed ${view.verification.confirmed}`} data-testid="canonical-scan-report-verified-confirmed" />
          <Chip size="small" label={`Cleared ${view.verification.cleared}`} data-testid="canonical-scan-report-cleared" />
          <Chip size="small" label={`Unresolved ${view.verification.unresolved}`} data-testid="canonical-scan-report-unresolved" />
        </Stack>
        {view.verification.unresolved > 0 ? (
          <Typography variant="body2" sx={{ mt: 1 }} data-testid="canonical-scan-report-unresolved-caveat">
            Unresolved observations are not counted as confirmed.
          </Typography>
        ) : null}
        {view.verification.lateArrivals > 0 ? (
          <Typography variant="body2" sx={{ mt: 0.75 }} data-testid="canonical-scan-report-late-arrivals">
            Late arrivals: {view.verification.lateArrivals}
          </Typography>
        ) : null}
        {!view.verification.complete ? (
          <Alert severity="info" sx={{ mt: 1 }} data-testid="canonical-scan-report-not-fully-verified">
            This Safety Scan is not fully verified.
          </Alert>
        ) : null}
      </Section>

      <Section id="where-when" kicker="B" title="Where / When">
        <Fact label="WHERE" value={view.whereLabel} testId="canonical-scan-report-where" />
        {view.siteLabel ? <Fact label="SITE" value={view.siteLabel} testId="canonical-scan-report-site" /> : null}
        {view.areaLabel ? <Fact label="AREA" value={view.areaLabel} testId="canonical-scan-report-area-fact" /> : null}
        {view.cameraLabel ? <Fact label="CAMERA VIEW" value={view.cameraLabel} testId="canonical-scan-report-camera" /> : null}
        <Fact label="MEASUREMENT SCOPE" value={view.measurementScopeLabel} testId="canonical-scan-report-scope" />
        <Fact label="WHEN" value={view.whenLabel} testId="canonical-scan-report-when" />
        <Fact label="Time concentration" value={view.concentrationLabel} testId="canonical-scan-report-concentration" />
        <Fact label="HOW OFTEN" value={view.howOftenLabel} testId="canonical-scan-report-how-often" />
      </Section>

      <Section id="measurement-basis" title="How it was measured">
        {view.measurementBasis.source === 'stored' ? (
          <Fact
            label="Stored definition"
            value={`${view.measurementBasis.areaLabel || 'Not recorded'} · ${view.measurementScopeLabel}`}
            testId="canonical-scan-report-stored-basis"
          />
        ) : (
          <Typography variant="body2" color="text.secondary" data-testid="canonical-scan-report-basis-missing">
            {view.measurementBasis.missingLabel}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" data-testid="canonical-scan-report-cm-boundary">
          {SCAN_MEASUREMENT_CLAIM_BOUNDARY}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="canonical-scan-report-cm-privacy">
          {CAMERA_MEASUREMENT_PRIVACY}
        </Typography>
        <Box sx={{ mt: 1.25 }}>
          <ScanCameraMeasurementBasis
            source={measurementSource}
            override={measurementOverride}
            defaultExpanded={measurementDefaultExpanded}
          />
        </Box>
      </Section>

      <Section id="control-relation" kicker="E" title="Control">
        <ScanControlRelationDisclosure relation={view.controlRelation} />
        {view.caseHref ? (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <Link component={RouterLink} to={view.caseHref} data-testid="canonical-scan-report-case-link">
              Open Safety Case Controls / Evaluation
            </Link>
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} data-testid="canonical-scan-report-evaluation-boundary">
          {view.evaluationLinkLabel}
        </Typography>
      </Section>

      {view.comparisonCopy ? (
        <Section id="governed-comparison" kicker="G" title="Governed comparison">
          <Typography variant="body2" data-testid="canonical-scan-report-comparison-copy">
            {view.comparisonCopy}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            A linked comparison does not prove that the Control caused the change.
          </Typography>
        </Section>
      ) : null}

      <Section id="limitations" title="Coverage and limitations">
        {view.limitations.length ? (
          <Stack spacing={0.5} data-testid="canonical-scan-report-limitations">
            {view.limitations.map((item) => (
              <Typography key={item} variant="body2">{item}</Typography>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">No additional Scan limitations recorded.</Typography>
        )}
      </Section>

      <Section id="evidence" title="Evidence">
        {view.evidence.length === 0 ? (
          <Typography variant="body2" color="text.secondary" data-testid="canonical-scan-report-evidence-empty">
            Representative reviewed evidence is not recorded for this Scan.
          </Typography>
        ) : (
          <Stack spacing={0.75} data-testid="canonical-scan-report-evidence">
            {view.evidence.map((item) => (
              <Typography key={item.memberId} variant="body2">
                {item.caption}
                {item.mediaAvailable ? '' : ' — media not available'}
              </Typography>
            ))}
          </Stack>
        )}
        {view.evidenceMissing ? (
          <Alert severity="info" sx={{ mt: 1 }} data-testid="canonical-scan-report-evidence-missing">
            Reviewed evidence was kept, but media is not available for these items.
          </Alert>
        ) : null}
      </Section>

      <Section id="governance" title="Governance">
        <Fact label="Status" value={view.lifecycle} testId="canonical-scan-report-governance-status" />
        <Fact label="Measurement period" value={view.windowLabel || 'Not recorded'} testId="canonical-scan-report-governance-window" />
        {view.signedRecordHref ? (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <Link component={RouterLink} to={view.signedRecordHref} data-testid="canonical-scan-report-signed-record-link">
              Open Signed Record
            </Link>
          </Typography>
        ) : null}
        <details style={{ marginTop: 8 }}>
          <summary>
            <Typography component="span" variant="caption" color="text.secondary">Technical identity</Typography>
          </summary>
          <Typography variant="body2" color="text.secondary" data-testid="canonical-scan-report-technical-id">
            Safety Scan {view.displayedReportId}
            {view.revisionLabel ? ` · revision ${view.revisionLabel}` : ''}
          </Typography>
        </details>
      </Section>
    </Stack>
  );
}

export default CanonicalSafetyScanReport;
